/**
 * Cloud dialogs (CLOUD_SYNC_SPEC.md §8.3): SignIn, SaveAs, Open, Publish.
 * Native DOM matching the UIManager panel style (design-system tokens).
 */
import { ICONS, phIcon } from '../icons.js';
import { AuthService } from '../../cloud/AuthService.js';
import { track } from '../../cloud/Analytics.js';

// --- Modal scaffolding ----------------------------------------------------

/**
 * Open a modal dialog. Returns {root, body, close} - callers fill `body`.
 * Escape / backdrop click / × close it (onClose fires exactly once).
 */
export function openModal({ title, width = 380, onClose = null }) {
    const overlay = document.createElement('div');
    overlay.className = 'sc-modal-overlay';

    const card = document.createElement('div');
    card.className = 'sc-modal-card';
    card.style.width = `${width}px`;

    const header = document.createElement('div');
    header.className = 'sc-modal-header';
    const h = document.createElement('div');
    h.className = 'sc-modal-title';
    h.textContent = title;
    const x = document.createElement('button');
    x.className = 'sc-modal-x';
    x.appendChild(phIcon(ICONS.x, 16));
    header.append(h, x);

    const body = document.createElement('div');
    body.className = 'sc-modal-body';
    card.append(header, body);
    overlay.appendChild(card);
    document.body.appendChild(overlay);

    let closed = false;
    const close = () => {
        if (closed) return;
        closed = true;
        overlay.remove();
        document.removeEventListener('keydown', onKey, true);
        onClose?.();
    };
    const onKey = (e) => {
        if (e.key === 'Escape') {
            e.stopPropagation();
            close();
        }
    };
    document.addEventListener('keydown', onKey, true);
    x.addEventListener('click', close);
    overlay.addEventListener('pointerdown', (e) => {
        if (e.target === overlay) close();
    });

    return { root: overlay, body, close };
}

/** One-time injection of the dialog / menu styles */
export function injectCloudStyles() {
    if (document.getElementById('sc-cloud-styles')) return;
    const style = document.createElement('style');
    style.id = 'sc-cloud-styles';
    style.textContent = `
        .sc-modal-overlay {
            position: fixed; inset: 0; z-index: 4000;
            background: rgba(20, 24, 26, 0.45);
            display: flex; align-items: center; justify-content: center;
            pointer-events: auto;
        }
        .sc-modal-card {
            background: #fff;
            border-radius: var(--radius-lg);
            box-shadow: var(--shadow-dock);
            max-width: calc(100vw - 32px);
            max-height: calc(100vh - 64px);
            display: flex; flex-direction: column;
            overflow: hidden;
        }
        .sc-modal-header {
            display: flex; align-items: center; gap: 8px;
            padding: 14px 16px 10px;
        }
        .sc-modal-title {
            flex: 1; font-size: 14px; font-weight: 600; color: var(--text);
        }
        .sc-modal-x {
            display: flex; align-items: center; justify-content: center;
            width: 26px; height: 26px; padding: 0; border: none;
            border-radius: 6px; background: transparent; color: #888;
            cursor: pointer;
        }
        .sc-modal-x:hover { background: rgba(0,0,0,0.06); color: #333; }
        .sc-modal-body {
            padding: 4px 16px 16px; overflow-y: auto;
            display: flex; flex-direction: column; gap: 10px;
            font-size: 13px; color: var(--text);
        }
        .sc-field { display: flex; flex-direction: column; gap: 4px; }
        .sc-field label {
            font-size: 10px; font-weight: 600; letter-spacing: 0.6px;
            text-transform: uppercase; color: var(--text-dim);
        }
        .sc-field input[type="text"], .sc-field input[type="email"],
        .sc-field input[type="password"], .sc-field textarea {
            font: inherit; font-size: 13px; color: var(--text);
            background: #fff; border: 1px solid #ccc; border-radius: 8px;
            padding: 7px 10px; outline: none;
        }
        .sc-field input:focus, .sc-field textarea:focus {
            border-color: var(--accent);
            box-shadow: 0 0 0 2px var(--accent-tint);
        }
        .sc-btn {
            display: inline-flex; align-items: center; justify-content: center;
            gap: 7px; padding: 8px 14px; border: none; border-radius: 8px;
            font-size: 13px; font-weight: 600; cursor: pointer;
            transition: background 0.15s ease, color 0.15s ease;
        }
        .sc-btn-primary { background: var(--accent); color: #fff; }
        .sc-btn-primary:hover { background: var(--accent-strong); }
        .sc-btn-primary:disabled { background: #b8dfe5; cursor: default; }
        .sc-btn-secondary {
            background: rgba(0,0,0,0.05); color: var(--text);
        }
        .sc-btn-secondary:hover { background: rgba(0,0,0,0.1); }
        .sc-btn-danger { background: transparent; color: #c0392b; }
        .sc-btn-danger:hover { background: rgba(192,57,43,0.08); }
        .sc-btn-row {
            display: flex; gap: 8px; justify-content: flex-end;
            margin-top: 4px;
        }
        .sc-error {
            color: #c0392b; font-size: 12px; min-height: 15px;
        }
        .sc-hint { color: var(--text-dim); font-size: 12px; }
        .sc-divider {
            display: flex; align-items: center; gap: 10px;
            color: var(--text-dim); font-size: 11px;
        }
        .sc-divider::before, .sc-divider::after {
            content: ''; flex: 1; height: 1px; background: rgba(0,0,0,0.1);
        }

        /* Open dialog design grid */
        .sc-design-grid {
            display: grid; grid-template-columns: 1fr 1fr; gap: 10px;
        }
        .sc-design-card {
            display: flex; flex-direction: column; gap: 0;
            border: 1px solid rgba(0,0,0,0.08); border-radius: 10px;
            overflow: hidden; cursor: pointer; background: #fff;
            text-align: left; padding: 0;
            transition: border-color 0.12s ease, box-shadow 0.12s ease;
        }
        .sc-design-card:hover {
            border-color: var(--accent);
            box-shadow: 0 2px 10px rgba(0,184,207,0.18);
        }
        .sc-design-thumb {
            width: 100%; aspect-ratio: 4 / 3; object-fit: cover;
            background: #eef3f4; display: block;
        }
        .sc-design-thumb-empty {
            width: 100%; aspect-ratio: 4 / 3; background: #eef3f4;
            display: flex; align-items: center; justify-content: center;
            color: #b5c4c7;
        }
        .sc-design-meta { padding: 8px 10px; }
        .sc-design-name {
            font-size: 12px; font-weight: 600; color: var(--text);
            overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
        }
        .sc-design-sub {
            font-size: 10px; color: var(--text-dim); margin-top: 2px;
            display: flex; align-items: center; gap: 6px;
        }
        .sc-pub-badge {
            font-size: 9px; font-weight: 700; letter-spacing: 0.4px;
            padding: 1px 6px; border-radius: 7px;
            background: var(--accent-tint); color: var(--accent-strong);
        }
        .sc-design-card .sc-design-del {
            opacity: 0; position: absolute; top: 6px; right: 6px;
        }
        .sc-design-card:hover .sc-design-del { opacity: 1; }

        /* Radio/check rows in the publish dialog */
        .sc-choice {
            display: flex; align-items: flex-start; gap: 8px;
            font-size: 13px; color: var(--text); cursor: pointer;
        }
        .sc-choice input { margin-top: 2px; accent-color: var(--accent); }
        .sc-choice .sub { font-size: 11px; color: var(--text-dim); }
        .sc-banner {
            background: var(--accent-tint); color: var(--accent-strong);
            border-radius: 8px; padding: 8px 10px; font-size: 12px;
        }
        .sc-share-row {
            display: flex; gap: 6px; align-items: center;
        }
        .sc-share-row input {
            flex: 1; font-size: 12px; color: var(--text);
            border: 1px solid #ccc; border-radius: 8px; padding: 6px 8px;
            background: #f7fafa;
        }

        /* Top-bar dropdown menus (File / account) */
        .sc-menu {
            position: absolute; min-width: 218px; z-index: 3500;
            background: var(--surface);
            backdrop-filter: blur(18px) saturate(1.4);
            -webkit-backdrop-filter: blur(18px) saturate(1.4);
            border: 1px solid rgba(0,0,0,0.08);
            border-radius: 12px; box-shadow: var(--shadow-dock);
            padding: 6px; pointer-events: auto;
        }
        .sc-menu-item {
            display: flex; align-items: center; gap: 10px;
            width: 100%; padding: 7px 10px; border: none;
            border-radius: 8px; background: transparent;
            font-size: 13px; color: var(--text); cursor: pointer;
            text-align: left;
        }
        .sc-menu-item:hover { background: var(--accent-tint); color: var(--accent-strong); }
        .sc-menu-item:disabled { color: #bbb; cursor: default; }
        .sc-menu-item:disabled:hover { background: transparent; }
        .sc-menu-item .sc-kbd {
            margin-left: auto; font-size: 11px; color: var(--text-dim);
        }
        .sc-menu-sep { height: 1px; background: rgba(0,0,0,0.08); margin: 5px 8px; }
        .sc-menu-label {
            padding: 6px 10px 2px; font-size: 11px; color: var(--text-dim);
            overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
        }
    `;
    document.head.appendChild(style);
}

// --- Sign in ----------------------------------------------------------------

/**
 * Sign-in / register dialog. Resolves the returned promise with the user
 * (or null if dismissed).
 * @param {AuthService} auth
 */
export function showSignInDialog(auth, { reason = null } = {}) {
    return new Promise((resolve) => {
        let settled = false;
        const settle = (user) => {
            if (!settled) { settled = true; resolve(user); }
        };
        const { body, close } = openModal({
            title: 'Sign in to SketchChair',
            width: 340,
            onClose: () => settle(auth.currentUser)
        });

        if (reason) {
            const hint = document.createElement('div');
            hint.className = 'sc-hint';
            hint.textContent = reason;
            body.appendChild(hint);
        }

        const err = document.createElement('div');
        err.className = 'sc-error';

        const googleBtn = document.createElement('button');
        googleBtn.className = 'sc-btn sc-btn-secondary';
        googleBtn.style.justifyContent = 'center';
        googleBtn.innerHTML =
            `<svg width="16" height="16" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
            <span>Continue with Google</span>`;
        googleBtn.addEventListener('click', async () => {
            err.textContent = '';
            try {
                const user = await auth.signInGoogle();
                settle(user);
                close();
            } catch (e) {
                err.textContent = AuthService.friendlyError(e);
            }
        });

        const divider = document.createElement('div');
        divider.className = 'sc-divider';
        divider.textContent = 'or use email';

        const emailField = document.createElement('div');
        emailField.className = 'sc-field';
        emailField.innerHTML = '<label>Email</label>';
        const email = document.createElement('input');
        email.type = 'email';
        email.autocomplete = 'email';
        emailField.appendChild(email);

        const passField = document.createElement('div');
        passField.className = 'sc-field';
        passField.innerHTML = '<label>Password</label>';
        const pass = document.createElement('input');
        pass.type = 'password';
        pass.autocomplete = 'current-password';
        passField.appendChild(pass);

        const row = document.createElement('div');
        row.className = 'sc-btn-row';
        row.style.justifyContent = 'space-between';
        const registerBtn = document.createElement('button');
        registerBtn.className = 'sc-btn sc-btn-secondary';
        registerBtn.textContent = 'Register';
        const signInBtn = document.createElement('button');
        signInBtn.className = 'sc-btn sc-btn-primary';
        signInBtn.textContent = 'Sign in';
        row.append(registerBtn, signInBtn);

        const emailAction = async (register) => {
            err.textContent = '';
            if (!email.value || !pass.value) {
                err.textContent = 'Enter an email and password';
                return;
            }
            signInBtn.disabled = true;
            try {
                const user = register
                    ? await auth.registerEmail(email.value, pass.value)
                    : await auth.signInEmail(email.value, pass.value);
                settle(user);
                close();
            } catch (e) {
                err.textContent = AuthService.friendlyError(e);
            } finally {
                signInBtn.disabled = false;
            }
        };
        signInBtn.addEventListener('click', () => emailAction(false));
        registerBtn.addEventListener('click', () => emailAction(true));
        pass.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') emailAction(false);
        });

        body.append(googleBtn, divider, emailField, passField, err, row);
        email.focus();
    });
}

// --- Save As -----------------------------------------------------------------

/**
 * Save As dialog (name + comment). Resolves {name, comment} or null.
 */
export function showSaveAsDialog({ title = 'Save to cloud', name = '', comment = '', cta = 'Save' } = {}) {
    return new Promise((resolve) => {
        let settled = false;
        const settle = (v) => { if (!settled) { settled = true; resolve(v); } };
        const { body, close } = openModal({
            title, width: 340, onClose: () => settle(null)
        });

        const nameField = document.createElement('div');
        nameField.className = 'sc-field';
        nameField.innerHTML = '<label>Name</label>';
        const nameInput = document.createElement('input');
        nameInput.type = 'text';
        nameInput.placeholder = 'My chair';
        nameInput.value = name || '';
        nameField.appendChild(nameInput);

        const commentField = document.createElement('div');
        commentField.className = 'sc-field';
        commentField.innerHTML = '<label>Comment (optional)</label>';
        const commentInput = document.createElement('textarea');
        commentInput.rows = 2;
        commentInput.value = comment || '';
        commentField.appendChild(commentInput);

        const row = document.createElement('div');
        row.className = 'sc-btn-row';
        const cancel = document.createElement('button');
        cancel.className = 'sc-btn sc-btn-secondary';
        cancel.textContent = 'Cancel';
        cancel.addEventListener('click', () => { settle(null); close(); });
        const save = document.createElement('button');
        save.className = 'sc-btn sc-btn-primary';
        save.textContent = cta;
        const submit = () => {
            settle({
                name: nameInput.value.trim() || null,
                comment: commentInput.value.trim() || null
            });
            close();
        };
        save.addEventListener('click', submit);
        nameInput.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') submit();
        });
        row.append(cancel, save);

        body.append(nameField, commentField, row);
        nameInput.focus();
        nameInput.select();
    });
}

// --- Open --------------------------------------------------------------------

const relTime = (iso) => {
    const s = (Date.now() - new Date(iso).getTime()) / 1000;
    if (s < 90) return 'just now';
    if (s < 3600) return `${Math.round(s / 60)}m ago`;
    if (s < 86400) return `${Math.round(s / 3600)}h ago`;
    if (s < 86400 * 30) return `${Math.round(s / 86400)}d ago`;
    return new Date(iso).toLocaleDateString();
};

/**
 * Open dialog: grid of the user's designs (thumb, name, updated,
 * Published badge). Resolves the chosen design id or null.
 * @param {import('../../cloud/DesignService.js').DesignService} designs
 */
export function showOpenDialog(designs) {
    return new Promise(async (resolve) => {
        let settled = false;
        const settle = (v) => { if (!settled) { settled = true; resolve(v); } };
        const { body, close } = openModal({
            title: 'Your designs', width: 460, onClose: () => settle(null)
        });

        const status = document.createElement('div');
        status.className = 'sc-hint';
        status.textContent = 'Loading…';
        body.appendChild(status);

        let list;
        try {
            list = await designs.list();
        } catch (e) {
            status.textContent = `Could not load designs: ${e.message}`;
            return;
        }
        if (list.length === 0) {
            status.textContent = 'No saved designs yet — draw a chair and hit Save (⌘S).';
            return;
        }
        status.remove();

        const grid = document.createElement('div');
        grid.className = 'sc-design-grid';
        body.appendChild(grid);

        for (const d of list) {
            const card = document.createElement('button');
            card.className = 'sc-design-card';
            card.style.position = 'relative';

            if (d.has_thumb && d.latest_version > 0) {
                const img = document.createElement('img');
                img.className = 'sc-design-thumb';
                img.alt = '';
                designs.thumbUrl(d.id, d.latest_version).then(url => {
                    if (url) img.src = url;
                });
                card.appendChild(img);
            } else {
                const empty = document.createElement('div');
                empty.className = 'sc-design-thumb-empty';
                empty.appendChild(phIcon(ICONS.officeChair, 28));
                card.appendChild(empty);
            }

            const meta = document.createElement('div');
            meta.className = 'sc-design-meta';
            const nameEl = document.createElement('div');
            nameEl.className = 'sc-design-name';
            nameEl.textContent = d.name || 'Untitled';
            const sub = document.createElement('div');
            sub.className = 'sc-design-sub';
            sub.textContent = `v${d.latest_version} · ${relTime(d.updated_at)}`;
            if (d.published) {
                const badge = document.createElement('span');
                badge.className = 'sc-pub-badge';
                badge.textContent = 'Published';
                sub.appendChild(badge);
            }
            meta.append(nameEl, sub);
            card.appendChild(meta);

            const del = document.createElement('button');
            del.className = 'sc-modal-x sc-design-del';
            del.title = 'Delete design';
            del.appendChild(phIcon(ICONS.trash, 14));
            del.addEventListener('click', async (e) => {
                e.stopPropagation();
                if (!confirm(`Delete "${d.name || 'Untitled'}" and all its versions?` +
                    (d.published ? ' This also removes the published copy.' : ''))) return;
                try {
                    await designs.remove(d.id);
                    card.remove();
                } catch (err2) {
                    alert(`Could not delete: ${err2.message}`);
                }
            });
            card.appendChild(del);

            card.addEventListener('click', () => { settle(d.id); close(); });
            grid.appendChild(card);
        }
    });
}

// --- Admin: library review -----------------------------------------------------

/**
 * Moderation queue (admins only): approve or reject library submissions.
 * Reject demotes to link-only (the author's share link keeps working).
 * @param {Object} ctx - {client, app}
 */
export async function showAdminReviewDialog({ client, app }) {
    const { body } = openModal({ title: 'Library submissions', width: 460 });

    const status = document.createElement('div');
    status.className = 'sc-hint';
    status.textContent = 'Loading…';
    body.appendChild(status);

    let pending;
    try {
        ({ pending } = await client.get('/admin/pending'));
    } catch (e) {
        status.textContent = `Could not load queue: ${e.message}`;
        return;
    }
    if (pending.length === 0) {
        status.textContent = 'Nothing waiting for approval 🎉';
        return;
    }
    status.remove();

    for (const p of pending) {
        const row = document.createElement('div');
        row.style.cssText = `
            display: flex; gap: 10px; align-items: center;
            border: 1px solid rgba(0,0,0,0.08); border-radius: 10px;
            padding: 8px;`;

        const thumb = document.createElement(p.has_thumb ? 'img' : 'div');
        thumb.style.cssText = `
            width: 84px; aspect-ratio: 4 / 3; object-fit: cover;
            border-radius: 6px; background: #eef3f4; flex-shrink: 0;`;
        if (p.has_thumb) thumb.src = `${client.base}/d/${p.slug}/thumb`;

        const meta = document.createElement('div');
        meta.style.cssText = 'flex: 1; min-width: 0;';
        const name = document.createElement('div');
        name.className = 'sc-design-name';
        name.textContent = p.title || 'Untitled';
        const sub = document.createElement('div');
        sub.className = 'sc-design-sub';
        sub.textContent = `by ${p.author} · v${p.version}`;
        const view = document.createElement('a');
        view.href = `${client.base}/d/${p.slug}`;
        view.target = '_blank';
        view.textContent = 'View';
        view.style.cssText = 'font-size: 11px; color: var(--accent-strong);';
        meta.append(name, sub, view);

        const actions = document.createElement('div');
        actions.style.cssText = 'display: flex; gap: 6px; flex-shrink: 0;';
        const verdict = document.createElement('span');
        verdict.className = 'sc-hint';
        const act = async (action) => {
            try {
                await client.post(`/admin/review/${p.pubId}`, { action });
                actions.innerHTML = '';
                verdict.textContent = action === 'approve' ? 'Approved ✓' : 'Rejected';
                actions.appendChild(verdict);
            } catch (e) {
                app.showToast(`Review failed: ${e.message}`);
            }
        };
        const approve = document.createElement('button');
        approve.className = 'sc-btn sc-btn-primary';
        approve.textContent = 'Approve';
        approve.addEventListener('click', () => act('approve'));
        const reject = document.createElement('button');
        reject.className = 'sc-btn sc-btn-danger';
        reject.textContent = 'Reject';
        reject.title = 'Removes it from the library queue; the author\'s share link keeps working';
        reject.addEventListener('click', () => act('reject'));
        actions.append(approve, reject);

        row.append(thumb, meta, actions);
        body.appendChild(row);
    }
}

// --- Publish -----------------------------------------------------------------

/**
 * Publish dialog (spec §8.2). Handles first publish + roll, visibility,
 * allow-edit, first-publish handle prompt, share link display.
 * @param {Object} ctx - {publishSvc, designs, user, onPublished}
 */
export async function showPublishDialog({ publishSvc, designs, user, app }) {
    const designId = designs.currentDesignId;

    const { body, close } = openModal({ title: 'Publish design', width: 380 });

    const status = document.createElement('div');
    status.className = 'sc-hint';
    status.textContent = 'Loading…';
    body.appendChild(status);

    let state;
    try {
        state = await publishSvc.getState(designId);
    } catch (e) {
        status.textContent = `Could not load publish state: ${e.message}`;
        return;
    }
    status.remove();

    // Banner: current → next published version (D3)
    if (state.published) {
        const banner = document.createElement('div');
        banner.className = 'sc-banner';
        banner.textContent = `Currently published as v${state.current_version}. ` +
            `Publishing again will create v${state.current_version + 1} ` +
            `from your latest save (v${designs.currentVersion}).`;
        body.appendChild(banner);
        if (state.visibility === 'library' && !state.approved) {
            const pending = document.createElement('div');
            pending.className = 'sc-banner';
            pending.style.background = 'rgba(255,170,0,0.15)';
            pending.style.color = '#b47a00';
            pending.textContent = 'Waiting for library approval — ' +
                'your share link already works in the meantime.';
            body.appendChild(pending);
        }
    }
    if (designs.dirty) {
        const warn = document.createElement('div');
        warn.className = 'sc-banner';
        warn.style.background = 'rgba(255,170,0,0.15)';
        warn.style.color = '#b47a00';
        warn.textContent = 'You have unsaved changes - the published copy ' +
            `will be your last save (v${designs.currentVersion}). Save first to include them.`;
        body.appendChild(warn);
    }

    const titleField = document.createElement('div');
    titleField.className = 'sc-field';
    titleField.innerHTML = '<label>Title</label>';
    const titleInput = document.createElement('input');
    titleInput.type = 'text';
    titleInput.value = state.title || designs.currentName || '';
    titleInput.placeholder = 'My chair';
    titleField.appendChild(titleInput);

    const descField = document.createElement('div');
    descField.className = 'sc-field';
    descField.innerHTML = '<label>Description (optional)</label>';
    const descInput = document.createElement('textarea');
    descInput.rows = 2;
    descInput.value = state.description || designs.currentComment || '';
    descField.appendChild(descInput);

    // Visibility radios
    const visWrap = document.createElement('div');
    visWrap.className = 'sc-field';
    visWrap.innerHTML = '<label>Visibility</label>';
    const mkChoice = (name, value, checked, main, sub, type = 'radio') => {
        const lab = document.createElement('label');
        lab.className = 'sc-choice';
        const input = document.createElement('input');
        input.type = type;
        if (name) input.name = name;
        input.value = value;
        input.checked = checked;
        const text = document.createElement('span');
        text.innerHTML = `${main}<div class="sub">${sub}</div>`;
        lab.append(input, text);
        return { lab, input };
    };
    const visLibrary = mkChoice('sc-vis', 'library',
        (state.visibility || 'library') === 'library',
        'Share to the library', 'Anyone can find it in the public gallery');
    const visLink = mkChoice('sc-vis', 'link', state.visibility === 'link',
        'Anyone with the link', 'Unlisted - only people with the link can view');
    visWrap.append(visLibrary.lab, visLink.lab);

    const editChoice = mkChoice(null, 'edit', !!state.allow_edit,
        'Allow others to edit', 'Viewers can save their own copy (a fork) - your original is never changed',
        'checkbox');

    // First-publish handle prompt (spec §4 (?))
    let handleInput = null;
    if (!user?.handle && !state.published) {
        const handleField = document.createElement('div');
        handleField.className = 'sc-field';
        handleField.innerHTML = '<label>Author name (shown in the library)</label>';
        handleInput = document.createElement('input');
        handleInput.type = 'text';
        handleInput.placeholder = 'e.g. gregsaul';
        handleInput.value = (user?.display_name || '').toLowerCase().replace(/[^a-z0-9_-]/g, '');
        handleField.appendChild(handleInput);
        body.appendChild(handleField);
    }

    const err = document.createElement('div');
    err.className = 'sc-error';

    const row = document.createElement('div');
    row.className = 'sc-btn-row';
    if (state.published) {
        const unpub = document.createElement('button');
        unpub.className = 'sc-btn sc-btn-danger';
        unpub.textContent = 'Unpublish';
        unpub.addEventListener('click', async () => {
            if (!confirm('Remove the published copy? The share link will stop working.')) return;
            try {
                await publishSvc.unpublish(state.pubId);
                app.showToast('Design unpublished');
                close();
            } catch (e) {
                err.textContent = e.message;
            }
        });
        row.appendChild(unpub);
        const spacer = document.createElement('div');
        spacer.style.flex = '1';
        row.appendChild(spacer);
    }
    const publishBtn = document.createElement('button');
    publishBtn.className = 'sc-btn sc-btn-primary';
    publishBtn.append(phIcon(ICONS.paperPlaneTilt, 15),
        document.createTextNode(state.published ? 'Publish update' : 'Publish'));
    row.appendChild(publishBtn);

    body.append(titleField, descField, visWrap, editChoice.lab, err, row);

    publishBtn.addEventListener('click', async () => {
        err.textContent = '';
        publishBtn.disabled = true;
        try {
            const result = await publishSvc.publish(designId, {
                visibility: visLink.input.checked ? 'link' : 'library',
                allow_edit: editChoice.input.checked,
                title: titleInput.value.trim() || null,
                description: descInput.value.trim() || null,
                handle: handleInput?.value.trim() || undefined
            });
            track('design_publish', {
                visibility: result.visibility,
                allow_edit: !!result.allow_edit,
                version: result.version
            });
            // Swap dialog content for the share link
            body.innerHTML = '';
            const done = document.createElement('div');
            done.className = 'sc-banner';
            done.textContent = `Published v${result.version} 🎉` +
                (result.visibility === 'library' && !result.approved
                    ? ' It will appear in the public library once approved - the share link works right away.'
                    : '');
            const shareField = document.createElement('div');
            shareField.className = 'sc-field';
            shareField.innerHTML = '<label>Share link</label>';
            const shareRow = document.createElement('div');
            shareRow.className = 'sc-share-row';
            const urlInput = document.createElement('input');
            urlInput.readOnly = true;
            urlInput.value = publishSvc.shareUrl(result);
            const copyBtn = document.createElement('button');
            copyBtn.className = 'sc-btn sc-btn-secondary';
            copyBtn.append(phIcon(ICONS.copySimple, 15));
            copyBtn.title = 'Copy link';
            copyBtn.addEventListener('click', () => {
                navigator.clipboard?.writeText(urlInput.value);
                app.showToast('Link copied');
                track('share', { method: 'copy_link', content_type: 'design' });
            });
            shareRow.append(urlInput, copyBtn);
            shareField.appendChild(shareRow);
            const closeRow = document.createElement('div');
            closeRow.className = 'sc-btn-row';
            const doneBtn = document.createElement('button');
            doneBtn.className = 'sc-btn sc-btn-primary';
            doneBtn.textContent = 'Done';
            doneBtn.addEventListener('click', close);
            closeRow.appendChild(doneBtn);
            body.append(done, shareField, closeRow);
            urlInput.focus();
            urlInput.select();
        } catch (e) {
            if (e.code === 'handle_taken') {
                err.textContent = 'That author name is taken - try another';
            } else if (e.code === 'nothing_saved') {
                err.textContent = 'Save the design (⌘S) before publishing';
            } else {
                err.textContent = e.message;
            }
        } finally {
            publishBtn.disabled = false;
        }
    });
}
